import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../redux/store";
import { clearSelectedProduct } from "../../redux/slices/productDetailSlice";
import { useTranslation } from "react-i18next";
import CustomOffcanvas from "../CustomOffcanvas";
import { Row, Col, Container } from "react-bootstrap";
import StyledButton from "../StyledButton";
import { useCart } from "../../hooks";

const ProductQuickView: React.FC = () => {
  const { t } = useTranslation(["productPage", "common"]);
  const dispatch = useDispatch();
  const product = useSelector((state: RootState) => state.productDetail.selectedProduct);
  const { addItemToCart } = useCart();
  const [quantity, setQuantity] = useState(1);

  if (!product) return null;

  const handleClose = () => dispatch(clearSelectedProduct());

  const sectionStyle: React.CSSProperties = { marginBottom: "20px" };
  const contentStyle: React.CSSProperties = {
    fontFamily: "Avenir Next Codensed Regular",
    fontWeight: 300,
    fontSize: "20px",
    lineHeight: "24px",
    color: "#342714",
    marginTop: "1.5rem",
  };

  const headlineStyle: React.CSSProperties = {
    fontWeight: 700,
    fontSize: "1.2rem",
    textTransform: "uppercase",
    color: "#c39d63",
    marginBottom: 10,
    fontFamily: "Avenir Next Codensed Ultralight",
  };

  const subheadlineStyle: React.CSSProperties = {
    fontSize: "2.6rem",
    fontWeight: 600,
    fontFamily: "Avenir Next Codensed Ultralight",
    color: "#514633",
    marginBottom: 25,
  };

  const priceStyle: React.CSSProperties = {
    fontSize: "2rem",
    color: "#c39d63",
    fontWeight: 700,
    fontFamily: "Avenir Next Codensed Ultralight",
  };

  const selectStyle: React.CSSProperties = {
    border: "1px solid #d3d7da",
    borderRadius: "0px",
    fontSize: "16px",
    padding: "10px",
    marginTop: "40px",
    fontFamily: "Avenir Next Codensed Regular",
    color: "#342714",
  };

  const badgeStyle: React.CSSProperties = {
    fontSize: "14px",
    borderRadius: "15px",
    padding: "6px 14px",
    fontFamily: "Avenir Next Codensed Regular",
    fontWeight: 500,
    backgroundColor: "#F6F4F2",
    border: "1px solid #c39d63",
    color: "#514633",
    marginRight: 10,
    marginBottom: 10,
    display: "inline-block",
  };

  const handleOnChangeQuantity = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setQuantity(Number(e.target.value));
  }
  
  return (
    <CustomOffcanvas
      show={!!product}
      onHide={handleClose}
      title={""}
      //imageUrl={product?.image}
      imageUrl={'https://bhmm.de/api/v1/images/products/7cd7f82c16de05b7a29b4079b57f24a3'}
    >
      <Container>
        <Row>
          <Col sm={12}>
            <h2 style={subheadlineStyle}>
              {product.name}
            </h2>
          </Col>

          <Col md={6} sm={12}>
            <div style={{ marginTop: 20 }}>
              <div style={priceStyle}>{`${product.currency} ${product.price}`}</div>
              <span>{t("product.price_note", { tax: product.taxPercentage })}</span>
            </div>

            <div style={{ marginTop: 20, display: "flex" }}>
              <select
                style={selectStyle}
                value={quantity}
                onChange={(e) => (handleOnChangeQuantity(e))}
              >
                {product.quantityOptions?.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>

              <StyledButton
                title={t("product.add_to_cart")}
                onClick={() => addItemToCart(product.productId, 1)}
                isRevert={true}
              />
            </div>

            {(product.isBiological || product.isVegan || product.isVegetarian) && (
              <div style={{ marginTop: 20 }}>
                {product.isBiological && <span style={badgeStyle}>{t("product.badges.bio")}</span>}
                {product.isVegan && <span style={badgeStyle}>{t("product.badges.vegan")}</span>}
                {product.isVegetarian && <span style={badgeStyle}>{t("product.badges.vegetarian")}</span>}
              </div>
            )}
          </Col>

          <Col md={6} sm={12}>
            <div style={sectionStyle}>
              <p style={contentStyle}>{product.description}</p>
            </div>

            {product.ingredients && (
              <div style={sectionStyle}>
                <h4 style={headlineStyle}>{t("product.ingredients.title")}</h4>
                <p style={contentStyle}>{product.ingredients}</p>
              </div>
            )}

            {product.allergenes && (
              <div style={sectionStyle}>
                <h4 style={headlineStyle}>{t("product.allergenes.title")}</h4>
                <p style={contentStyle}>{product.allergenes}</p>
              </div>
            )}
          </Col>
        </Row>

        {product.nutrition && product.nutrition.length > 0 && (
          <div style={{ marginTop: "2rem" }}>
            <h4 style={headlineStyle}>{t("product.nutrition.title")}</h4>
            <p style={contentStyle}>{t("product.nutrition.header")}</p>
            {product.nutrition.map((n) => (
              <div key={n.name} style={{
                ...contentStyle,
                display: "flex",
                justifyContent: "space-between",
                borderBottom: "1px solid #ccc",
                paddingBottom: "6px",
                marginTop: "0.75rem",
              }}>
                <span>{n.name}</span>
                <span>{n.value}</span>
              </div>
            ))}
          </div>
        )}
      </Container>
    </CustomOffcanvas>
  );
};

export default ProductQuickView;